import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  addFilter,
  removeFilter,
  toast,
  useToast,
} from '../../../../../../../../../redux/action';
import { AddCircleOutline, CancelOutlined } from '@material-ui/icons';
import SelectDropDown from '../_shared/selectDropdown';
import Container from '../_shared/container';
import Text from '../_shared/text';
import Wrap from '../_shared/wrap';
import Bold from '../_shared/bold';
import Content from '../../../_shared/content';
import FitContent from '../_shared/fitContent';
import Space from '../_shared/space';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import { Select } from 'antd';
import { ErrorMsg } from './committeeMember.styled';
import SelectStyled from '../_shared/select.styled';
import ButtonStyled from '../_shared/button';

export const CommitteeMember = ({
  checked,
  selectedFilters = [],
  filterName,
  memberships,
  committees,
  orgTypeName,
}) => {
  const uniqueMembershipsOrAssociations = [
    ...new Set(committees.map(committee => committee.membershipId)),
  ];
  const availableMembershipsOrAssociations = memberships.filter(mem =>
    uniqueMembershipsOrAssociations.includes(mem.amsId)
  );
  const [selectedMembership, setSelectedMembership] = useState({});
  const [selectedCommittee, setSelectedCommittee] = useState({});
  const [selectedYear, setSelectedYear] = useState(new Date());
  const [savedCommittees, setSavedCommittees] = useState(selectedFilters ?? []);

  const dispatch = useDispatch();

  const { errorToast, successToast } = useToast();

  const committeeOptions = useMemo(() => {
    setSelectedCommittee({});
    return committees?.filter(
      committee => committee.membershipId === selectedMembership.amsId
    );
  }, [selectedMembership, committees]);

  const membershipOrAssociation = useMemo(
    () => (orgTypeName === 'magic' ? 'Association' : 'Membership'),
    [orgTypeName]
  );

  useEffect(() => {
    if (savedCommittees.length) {
      dispatch(addFilter({ [filterName]: savedCommittees }));
    } else if (!savedCommittees?.length && selectedFilters?.length) {
      dispatch(removeFilter(filterName));
    }
  }, [savedCommittees]);

  useEffect(() => {
    if (!checked && savedCommittees.length) {
      dispatch(removeFilter(filterName));
    }
  }, [checked]);

  const handleMembershipChange = ({ target }) => {
    const membershipName = target.value;
    const matchingMembership = memberships.find(
      mem => mem.name.trim() === membershipName.trim()
    );
    if (!matchingMembership) {
      dispatch(toast('error', 'Error selecting Membership.'));
      return;
    }
    setSelectedMembership(matchingMembership);
  };

  const handleCommitteeChange = value => {
    const selectedCommittee = committees.find(committee => {
      const comparator =
        orgTypeName === 'magic'
          ? committee.committeeDescription
          : committee.committeeId;
      return comparator === value;
    });
    setSelectedCommittee({
      id: selectedCommittee.committeeId,
      description: selectedCommittee.committeeDescription || '',
    });
  };

  const handleYearChange = target => setSelectedYear(target);

  const isNewConditionOverlapsWithExistingCondition = (
    currentCommittee,
    newSelectedMembership,
    newSelectedYear
  ) => {
    return (
      currentCommittee.membership === newSelectedMembership &&
      currentCommittee.year === newSelectedYear
    );
  };

  const isNewConditionExactDuplicate = (
    currentCommittee,
    newSelectedMembership,
    newSelectedYear
  ) => {
    return (
      currentCommittee.membership === newSelectedMembership &&
      currentCommittee.committee === selectedCommittee.id &&
      currentCommittee.year === newSelectedYear
    );
  };

  const addCommittee = () => {
    const errorMessage = [];
    if (!Object.keys(selectedMembership).length) {
      errorMessage.push('Please select Membership.');
    }
    if (!selectedCommittee?.id) {
      errorMessage.push('Please select Committee.');
    }
    if (!selectedYear) {
      errorMessage.push('Please select committee year.');
    }

    if (errorMessage.length) {
      errorToast(errorMessage.join('\n'));
    } else {
      const selectedFullYear = new Date(selectedYear).getFullYear();

      // Note: savedCommittees structure = [
      //   {
      //     membership: selectedMembership.amsId,
      //     committee: selectedCommittee.id,
      //     year: selectedFullYear,
      //   },
      // ];

      // Note, when current selection has any overlap with existing saved committees then it will override the existing selection.
      let overrideCurrentSelectionValue = '';
      setSavedCommittees(committees => {
        // Note: filter out any existing saved committees that have any overlap with current selection.
        const filteredCommittees = committees?.filter(committee => {
          let shouldSkip = false;
          //committee.committee === true || selectedCommittee.id === true means any/all committees for specific membership/association and year apply
          if (committee.committee === true || selectedCommittee.id === true) {
            shouldSkip = isNewConditionOverlapsWithExistingCondition(
              committee,
              selectedMembership.amsId,
              selectedFullYear
            );
            if (shouldSkip) {
              overrideCurrentSelectionValue += ` ${committee.membership}-${
                committee.committee === true ? 'Any/All' : committee.committee
              }-${committee.year}`;
            }
            return !shouldSkip;
          } else {
            shouldSkip = isNewConditionExactDuplicate(
              committee,
              selectedMembership.amsId,
              selectedFullYear
            );
            if (shouldSkip) {
              overrideCurrentSelectionValue += ` ${committee.membership}-${committee.committee}-${committee.year}`;
            }
            return !shouldSkip;
          }
        });

        if (overrideCurrentSelectionValue) {
          successToast(
            `There is an overlap of your previous selection ${overrideCurrentSelectionValue} with new values. New selection will override the previous selection.`
          );
        }

        filteredCommittees.push({
          membership: selectedMembership.amsId,
          committee: selectedCommittee.id,
          year: selectedFullYear,
        });

        return filteredCommittees;
      });

      setSelectedCommittee({});
      setSelectedMembership({});
      setSelectedYear(new Date());
    }
  };

  const handleRemoveCommittee = index => {
    setSavedCommittees(committees => [
      ...committees.slice(0, index),
      ...committees.slice(index + 1),
    ]);
  };

  const handleCheck = ({ target }) => {
    setSelectedCommittee({ id: target.checked, description: 'Any/All' });
  };

  const getSelectedCommittee = committee => {
    if (typeof committee === 'boolean') {
      return <Bold>Any/all</Bold>;
    }
    const selectedCommittee = committees.find(
      com => committee === com.committeeId
    );
    return (
      <Bold>
        {orgTypeName === 'magic'
          ? selectedCommittee?.committeeDescription
          : selectedCommittee?.committeeId}
      </Bold>
    );
  };

  const getMembershipName = membershipId => {
    const matchingMembership = memberships.find(
      mem => mem.amsId === membershipId
    );
    return matchingMembership?.name || membershipId;
  };

  return (
    <div>
      <Container>
        {selectedFilters.map(({ membership, committee, year }, index) => (
          <Wrap key={`${membership}-${index}`}>
            <FitContent>
              <Space> {index > 0 ? 'or' : '-'}</Space>
              {membershipOrAssociation}
            </FitContent>
            <Bold>{getMembershipName(membership)}</Bold>
            <FitContent>Committee </FitContent>
            {getSelectedCommittee(committee)}
            <FitContent>in </FitContent>
            <Bold>{year}</Bold>

            <CancelOutlined
              fontSize="small"
              style={{ fill: '#E63946' }}
              onClick={() => handleRemoveCommittee(index)}
            />
          </Wrap>
        ))}
        <Content>
          <Text>
            {savedCommittees.length ? 'or ' : ''}
            {membershipOrAssociation}
          </Text>
          <SelectDropDown
            value={selectedMembership.name || ''}
            menuItemNames={availableMembershipsOrAssociations.map(
              mem => mem.name
            )}
            onChange={handleMembershipChange}
          />
        </Content>
        <Content>
          <Text isDisabled={!committeeOptions?.length}>Committee </Text>
          <SelectStyled
            value={
              orgTypeName === 'magic'
                ? selectedCommittee?.description
                : selectedCommittee?.committeeId
            }
            onChange={handleCommitteeChange}
            disabled={
              !selectedMembership.name ||
              !committeeOptions?.length ||
              (typeof selectedCommittee?.id === 'boolean' &&
                selectedCommittee?.id)
            }
            showSearch
            filterOption={(input, option) =>
              option?.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {committeeOptions?.map(committee => (
              <Select.Option
                key={committee.committeeId}
                value={
                  orgTypeName === 'magic'
                    ? committee.committeeDescription
                    : committee.committeeId
                }
              >
                {`${committee.committeeId}${
                  orgTypeName === 'magic'
                    ? `- ${committee.committeeDescription}`
                    : ''
                }`}
              </Select.Option>
            ))}
          </SelectStyled>

          <Text isDisabled={!committeeOptions?.length}>{'or '}</Text>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={
                  typeof selectedCommittee?.id === 'boolean' &&
                  selectedCommittee?.id
                }
                onChange={handleCheck}
                label="Any/all"
                disabled={!committeeOptions?.length || !selectedMembership}
              />
            }
            label="Any/all"
          />
        </Content>
        <Content>
          <Text isDisabled={!committeeOptions?.length}>in </Text>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              style={{ width: '100px', top: '-4px' }}
              disableToolbar
              variant="inline"
              margin="normal"
              autoOk
              views={['year']}
              format="yyyy"
              placeholder="yyyy"
              value={selectedYear}
              onChange={handleYearChange}
              animateYearScrolling
              allowKeyboardControl
              disabled={!committeeOptions?.length}
              minDate={
                new Date(new Date().setFullYear(new Date().getFullYear() - 5))
              }
              maxDate={
                new Date(new Date().setFullYear(new Date().getFullYear() + 1))
              }
            />
          </MuiPickersUtilsProvider>
        </Content>
        <ButtonStyled
          variant="contained"
          size="small"
          startIcon={<AddCircleOutline />}
          disabled={!committeeOptions?.length}
          onClick={addCommittee}
        >
          Add
        </ButtonStyled>
      </Container>
      {!committeeOptions?.length && selectedMembership?.name && (
        <ErrorMsg>No committees found for {selectedMembership.name}</ErrorMsg>
      )}
    </div>
  );
};
